import type { ParsedTemplateEntry } from '@vivport/backend/types';
import { cleanCell, type SectionTable } from './section-tables';

// THE RULE, and its cost. templates.addEntry and templates.addTableRow carry no idempotency
// handle — both input schemas are .strict(), so one cannot be added from here. When a write fails
// ambiguously (drop, timeout, 5xx) it may or may not have landed, so before retrying we refetch
// the render and skip the write if a matching entry or row is already there.
// The cost: two genuinely identical entries or rows are indistinguishable, so a member who
// deliberately files the same defect twice, and hits an ambiguous failure on the second, gets the
// write skipped. That is accepted because it only applies on the retry-after-ambiguity path, where
// the alternative is a silent double-write the member never asked for and cannot see.

export function hasMatchingEntry(
  entries: readonly ParsedTemplateEntry[],
  candidate: { title: string; category?: string; values: Record<string, string> }
): boolean {
  const title = candidate.title.trim();
  const category = candidate.category?.trim() ?? null;
  const keys = Object.keys(candidate.values);
  return entries.some((entry) => {
    if (entry.title.trim() !== title) return false;
    if ((entry.category?.trim() ?? null) !== category) return false;
    if (Object.keys(entry.values).length !== keys.length) return false;
    return keys.every((key) => (entry.values[key] ?? '').trim() === (candidate.values[key] ?? '').trim());
  });
}

// A null cell is a column the caller cannot predict (the photo column, whose rendered value is a
// ref the client never composed) and is not compared.
export function hasMatchingRow(table: SectionTable | undefined, cells: readonly (string | null)[]): boolean {
  if (table === undefined) return false;
  const wanted = cells.map((cell) => (cell === null ? null : cleanCell(cell)));
  return table.rows.some(
    (row) =>
      row.length === wanted.length &&
      row.every((cell, index) => wanted[index] === null || cleanCell(cell) === wanted[index])
  );
}
